// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
//
// Pure layout maths for the hand-rolled SVG charts. No DOM. Position-tested.

export interface Bar { x: number; y: number; w: number; h: number; index: number }

/**
 * Evenly spaced vertical bars across `width`, heights proportional to each
 * count over `maxOverride ?? max(counts)`. Columns are flush (contiguous slots)
 * with a symmetric `gap` carved out of each slot, and every bar stays within
 * [0,width]×[0,height]. `logBase` (>0) plots log-scaled heights for count>0.
 */
export function histBars(
  counts: number[],
  width: number,
  height: number,
  gap = 2,
  maxOverride?: number,
  log = false,
): Bar[] {
  const n = counts.length;
  const bars: Bar[] = [];
  if (n === 0 || width <= 0 || height <= 0) return bars;
  const slot = width / n;
  const scaleVal = (c: number) => (log ? (c > 0 ? Math.log10(c + 1) : 0) : c);
  const max = maxOverride !== undefined ? scaleVal(maxOverride) : Math.max(...counts.map(scaleVal), 0);
  for (let i = 0; i < n; i++) {
    const x = i * slot + gap / 2;
    const w = Math.max(0, slot - gap);
    const frac = max > 0 ? scaleVal(counts[i]) / max : 0;
    const h = Math.max(0, Math.min(1, frac)) * height;
    const y = height - h;
    bars.push({ x, y, w, h, index: i });
  }
  return bars;
}

export interface Pt { cx: number; cy: number; index: number }

/**
 * Map data points to pixel coordinates inside [0,width]×[0,height]. The y axis
 * is inverted (larger data y = higher on screen). Points are clamped in-bounds.
 */
export function scatterPoints(
  pts: Array<{ x: number; y: number }>,
  domX: [number, number],
  domY: [number, number],
  width: number,
  height: number,
): Pt[] {
  const [x0, x1] = domX;
  const [y0, y1] = domY;
  const sx = x1 - x0 || 1;
  const sy = y1 - y0 || 1;
  return pts.map((p, index) => {
    let cx = ((p.x - x0) / sx) * width;
    let cy = height - ((p.y - y0) / sy) * height;
    if (!Number.isFinite(cx)) cx = 0;
    if (!Number.isFinite(cy)) cy = height;
    cx = Math.max(0, Math.min(width, cx));
    cy = Math.max(0, Math.min(height, cy));
    return { cx, cy, index };
  });
}

export interface Cell { r: number; c: number; x: number; y: number; w: number; h: number }

/** Flush, non-overlapping grid cells for a matrix heatmap. */
export function gridCells(rows: number, cols: number, width: number, height: number, gap = 1): Cell[] {
  const cells: Cell[] = [];
  if (rows <= 0 || cols <= 0 || width <= 0 || height <= 0) return cells;
  const cw = width / cols;
  const ch = height / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({
        r,
        c,
        x: c * cw + gap / 2,
        y: r * ch + gap / 2,
        w: Math.max(0, cw - gap),
        h: Math.max(0, ch - gap),
      });
    }
  }
  return cells;
}

/**
 * Build an SVG polyline points string from a series, mapping data → pixels the
 * same way as `scatterPoints` (inverted y). Returns "x,y x,y …".
 */
export function polyline(
  values: number[],
  domY: [number, number],
  width: number,
  height: number,
): string {
  const n = values.length;
  if (n === 0) return '';
  const [y0, y1] = domY;
  const sy = y1 - y0 || 1;
  const step = n > 1 ? width / (n - 1) : 0;
  const parts: string[] = [];
  for (let i = 0; i < n; i++) {
    const x = i * step;
    let y = height - ((values[i] - y0) / sy) * height;
    if (!Number.isFinite(y)) y = height;
    y = Math.max(0, Math.min(height, y));
    parts.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  }
  return parts.join(' ');
}
